import commaNumber from 'comma-number';
import dayjs, { type Dayjs, type ManipulateType } from 'dayjs';

import {
  CHART_COLOR_SCALE,
  CHART_COMPARE_TERM_TYPE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { ChartTermUnitMap, ChartTopNumberMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { GreenBaseColor, RedBaseColor } from '@/styles/colors/baseColors';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { filterMap } from '@/utils/lazy';
import numberUtils from '@/utils/number';
import {
  DATE_FORMAT,
  getDateRangeWithEndDateOffset,
  getOpUnitTypeByTermUnit,
  getTermUnitLabel,
  getTimezoneDayjs,
  MONTH_FORMAT_MAP,
  parseWeeKFormat,
} from '@/utils/time';

const getFrequencyByTermUnit = (unit: CHART_TERM_UNIT) => {
  switch (unit) {
    case CHART_TERM_UNIT.HOUR:
      return CHART_FREQUENCY.HOUR;
    case CHART_TERM_UNIT.DAY:
      return CHART_FREQUENCY.DAY;
    case CHART_TERM_UNIT.WEEK:
      return CHART_FREQUENCY.WEEK;
    case CHART_TERM_UNIT.MONTH:
      return CHART_FREQUENCY.MONTH;
    case CHART_TERM_UNIT.QUARTER:
      return CHART_FREQUENCY.MONTH;
    default:
      throw new Error('Invalid unit');
  }
};

const getTimeRangeByTerm = (
  today: Dayjs,
  term: ChartTermDef,
): [Dayjs, Dayjs] => {
  if (term.type === CHART_TERM_TYPE.FIXED) {
    return [getTimezoneDayjs(term.start_date), getTimezoneDayjs(term.end_date)];
  }

  if (term.type === CHART_TERM_TYPE.PERIODIC) {
    const unit = getOpUnitTypeByTermUnit(term.unit);

    if (term.unit === CHART_TERM_UNIT.QUARTER) {
      const startDayOfQuarter = today.startOfQuarter();
      if (term.relative === CHART_PERIOD_RELATIVE.INCLUDE) {
        return [
          startDayOfQuarter.subtractQuarters(term.value - 1),
          startDayOfQuarter.endOfQuarter().startOf('day').addVal(1, 'day'),
        ];
      }
      if (term.relative === CHART_PERIOD_RELATIVE.EXCLUDE) {
        const previousQuarter = startDayOfQuarter.subtractQuarters(1);
        return [
          previousQuarter.subtractQuarters(term.value - 1),
          previousQuarter.endOfQuarter().startOf('day').addVal(1, 'day'),
        ];
      }
      throw new Error('Invalid period relative');
    } else {
      const startDayOfUnit = today.startOf(unit);
      if (term.relative === CHART_PERIOD_RELATIVE.INCLUDE) {
        // hour 단위: API가 value 시간 전부터 데이터를 반환하므로 별도 처리
        if (unit === 'hour') {
          return [
            startDayOfUnit.subtractVal(term.value, unit),
            startDayOfUnit,
          ];
        }
        return [
          startDayOfUnit.subtractVal(term.value - 1, unit),
          startDayOfUnit.endOf(unit).startOf('day').addVal(1, 'day'),
        ];
      }
      if (term.relative === CHART_PERIOD_RELATIVE.EXCLUDE) {
        if (unit === 'hour') {
          const previous = startDayOfUnit.subtractVal(1, unit);
          return [
            previous.subtractVal(term.value - 1, unit),
            startDayOfUnit,
          ];
        }
        const previous = startDayOfUnit.subtractVal(1, unit);
        return [
          previous.subtractVal(term.value - 1, unit),
          previous.endOf(unit).startOf('day').addVal(1, 'day'),
        ];
      }
      throw new Error('Invalid period relative');
    }
  }
  throw new Error('Invalid term type');
};

const getCompareTimeRangeByTerm = (
  today: Dayjs,
  term: ChartTermDef,
  compareTerm?: ChartCompareTermDef,
): [Dayjs, Dayjs] => {
  const [startDate, endDate] = getTimeRangeByTerm(today, term);
  const unit = getOpUnitTypeByTermUnit(term.unit);

  const value = startDate.diff(endDate, unit);

  if (!compareTerm) return [startDate, endDate];

  if (compareTerm.type === CHART_COMPARE_TERM_TYPE.PREVIOUS) {
    const range: [Dayjs, Dayjs] = [
      startDate.addVal(value, unit),
      endDate.addVal(value, unit),
    ];
    return range;
  }
  if (compareTerm.type === CHART_COMPARE_TERM_TYPE.YEAR_BEFORE) {
    if (term.unit === CHART_TERM_UNIT.WEEK) {
      const startWeek = startDate.week();
      const endWeek = endDate.week();
      return [
        startDate.addVal(-1, 'year').week(startWeek).startOf('week'),
        endDate.addVal(-1, 'year').week(endWeek).endOf('week').startOf('day'),
      ];
    }
    return [startDate.addVal(-1, 'year'), endDate.addVal(-1, 'year')];
  }
  if (compareTerm.type === CHART_COMPARE_TERM_TYPE.FIXED) {
    const startDate = getTimezoneDayjs(compareTerm.start_date);
    const endDate = getCustomCompareTermEndDate(startDate, term);
    return [startDate, endDate];
  }
  return [startDate, endDate];
};

const transformToYBased = (
  xBasedData: ChartCrossTableData,
): ChartReversedCrossTableData => {
  const uniqueYValues = Array.from(
    new Set(xBasedData.flatMap((item) => item.values.map((v) => v.y))),
  );

  return uniqueYValues.map((y) => ({
    y,
    values: xBasedData.map((xItem) => ({
      x: xItem.x,
      value: xItem.values.find((v) => v.y === y)?.value ?? 0,
    })),
  }));
};

export const getDateLabels = (
  dateRange: [Dayjs, Dayjs],
  formatCallback: (Dayjs: Dayjs) => string,
  unit: ManipulateType | 'quarter',
) => {
  let labels = [];
  let [startDate, endDate] = dateRange;
  if (unit === 'quarter') {
    startDate = startDate.startOfQuarter();
    endDate = endDate.subtractVal(1, 'day');
  } else if (unit === 'hour') {
    // 시간 단위는 1시간만 감산 (1일 감산하면 24시간분 누락)
    startDate = startDate.startOf(unit);
    endDate = endDate.subtractVal(1, 'hour');
  } else {
    startDate = startDate.startOf(unit);
    endDate = endDate.subtractVal(1, 'day');
  }
  let condition = true;
  while (condition) {
    labels.push(formatCallback(startDate));
    if (startDate.isBefore(endDate)) {
      if (unit === 'quarter') {
        startDate = startDate.addQuarters(1);
      } else {
        startDate = startDate.addVal(1, unit);
      }
      if (startDate.isAfter(endDate)) {
        condition = false;
      }
    } else {
      condition = false;
    }
  }
  return labels;
};

export const getFormatByFrequency = (frequency: CHART_FREQUENCY) => {
  switch (frequency) {
    case CHART_FREQUENCY.HOUR:
      return (dayjs: Dayjs) => dayjs.format('YYYY-MM-DD HH');
    case CHART_FREQUENCY.DAY:
      return (dayjs: Dayjs) => dayjs.format(DATE_FORMAT.base);
    case CHART_FREQUENCY.WEEK:
      return (dayjs: Dayjs) => `${dayjs.weekYear()} W${dayjs.week()}`;
    case CHART_FREQUENCY.MONTH:
      return (dayjs: Dayjs) => dayjs.format('MMM YY');
    default:
      return (dayjs: Dayjs) => dayjs.format(DATE_FORMAT.base);
  }
};

export const getDateLabelsByUnit = (props: {
  unit: CHART_TERM_UNIT;
  dateRange: [Dayjs, Dayjs] | null;
}) => {
  let labels: string[] = [];
  // 1: daily 2: weekly 3: monthly
  if (!props.dateRange) return labels;
  switch (props.unit) {
    case CHART_TERM_UNIT.HOUR:
      labels = getDateLabels(
        props.dateRange,
        getFormatByFrequency(CHART_FREQUENCY.HOUR),
        'hour',
      );
      break;
    case CHART_TERM_UNIT.DAY:
      labels = getDateLabels(
        props.dateRange,
        getFormatByFrequency(CHART_FREQUENCY.DAY),
        'day',
      );
      break;
    case CHART_TERM_UNIT.WEEK:
      labels = getDateLabels(
        props.dateRange,
        getFormatByFrequency(CHART_FREQUENCY.WEEK),
        'week',
      );
      break;
    case CHART_TERM_UNIT.MONTH:
      labels = getDateLabels(
        props.dateRange,
        getFormatByFrequency(CHART_FREQUENCY.MONTH),
        'month',
      );
      break;
    case CHART_TERM_UNIT.QUARTER:
      labels = getDateLabels(
        props.dateRange,
        getFormatByFrequency(CHART_FREQUENCY.MONTH),
        'quarter',
      );
      break;
    default:
      throw new Error(`Invalid unit ${props.unit}`);
  }

  return labels;
};

export const parseDateFormatByFrequency = (
  label: string, // daily: YYYY-MM-DD, weekly: YYYY, [W]WW, monthly: MMM YYYY
  frequency: CHART_FREQUENCY,
) => {
  // 라벨은 워크스페이스 타임존으로 포맷된 문자열이므로 getTimezoneDayjs로 해석
  if (frequency === CHART_FREQUENCY.HOUR) {
    const date = getTimezoneDayjs(label);
    return {
      start: date.startOf('hour').toJSON(),
      end: date.addVal(1, 'hour').startOf('hour').toJSON(),
    };
  }
  if (frequency === CHART_FREQUENCY.DAY) {
    const date = getTimezoneDayjs(label);
    return {
      start: date.startOf('day').toJSON(),
      end: date.addVal(1, 'day').startOf('day').toJSON(),
    };
  }
  if (frequency === CHART_FREQUENCY.WEEK) {
    const start = parseWeeKFormat(label);
    const result = {
      start: start.toJSON(),
      end: start.endOf('week').addVal(1, 'day').startOf('day').toJSON(),
    };

    return result;
  }

  if (frequency === CHART_FREQUENCY.MONTH) {
    const [monthStr, year] = label.split(' ');
    const standard = getTimezoneDayjs(
      `20${year}-${MONTH_FORMAT_MAP[monthStr]}-01`,
    );

    return {
      start: standard.startOf('month').toJSON(),
      end: standard.addVal(1, 'month').toJSON(),
    };
  }

  throw new Error('Invalid RangeUnit');
};

const getKeyValueMapByKeys = (dateLabels: StringNumberValue[]) => {
  return dateLabels.reduce(
    (acc, date) => {
      acc[date] = 0;
      return acc;
    },
    {} as Record<string, number>,
  );
};

const getUnitByFrequency = (frequency: CHART_FREQUENCY): CHART_TERM_UNIT => {
  switch (frequency) {
    case CHART_FREQUENCY.HOUR:
      return CHART_TERM_UNIT.HOUR;
    case CHART_FREQUENCY.DAY:
      return CHART_TERM_UNIT.DAY;
    case CHART_FREQUENCY.WEEK:
      return CHART_TERM_UNIT.WEEK;
    case CHART_FREQUENCY.MONTH:
      return CHART_TERM_UNIT.MONTH;
  }
};

const getColorScaleColor = (
  statistics: ColorScaleStatistics,
  value: number,
  colorScale: CHART_COLOR_SCALE,
  isPercentage?: boolean,
) => {
  const newStatistics = {
    max: isPercentage ? 100 : statistics.max,
    min: isPercentage ? 0 : statistics.min,
  };
  const getOffsetValue = () => {
    if (value < newStatistics.min) return 0;
    if (value > newStatistics.max) return newStatistics.max - newStatistics.min;
    return value - newStatistics.min;
  };
  const offsetValue = getOffsetValue();

  const divide = newStatistics.max - newStatistics.min;
  const middle = (newStatistics.max + newStatistics.min) / 2;
  const middleRange = middle - newStatistics.min;

  const alpha =
    offsetValue === 0 || divide === 0
      ? 0
      : Number((offsetValue / divide).toFixed(2));

  const midRangeAlpha =
    offsetValue === 0 || middleRange === 0
      ? 0
      : Number((offsetValue / 2 / middleRange).toFixed(2));

  switch (colorScale) {
    case CHART_COLOR_SCALE.WHITE_TO_GREEN:
      return getAlphaColor(GreenBaseColor.green500, alpha);
    case CHART_COLOR_SCALE.GREEN_TO_WHITE:
      return getAlphaColor(GreenBaseColor.green500, 1 - alpha);
    case CHART_COLOR_SCALE.WHITE_TO_RED:
      return getAlphaColor(RedBaseColor.red600, alpha);
    case CHART_COLOR_SCALE.RED_TO_WHITE:
      return getAlphaColor(RedBaseColor.red600, 1 - alpha);
    case CHART_COLOR_SCALE.GREEN_TO_RED:
      if (offsetValue < middle) {
        return getAlphaColor(GreenBaseColor.green500, 1 - midRangeAlpha);
      }
      return getAlphaColor(RedBaseColor.red600, midRangeAlpha);
    case CHART_COLOR_SCALE.RED_TO_GREEN:
      if (offsetValue < middle) {
        return getAlphaColor(RedBaseColor.red600, 1 - midRangeAlpha);
      }
      return getAlphaColor(GreenBaseColor.green500, midRangeAlpha);
  }
};

const formatNumber = (value: number) => {
  const integer = numberUtils.formatDecimal(value, 1);
  return commaNumber(integer);
};

const getTableType = (
  type: CHART_TYPE,
  historical: boolean,
): ChartTableType => {
  if (type === CHART_TYPE.CROSS_AXIS) return 'cross';
  if (historical) return 'cross';
  return 'single';
};

const getMaxRangeByTermUnit = (unit: CHART_TERM_UNIT) => {
  switch (unit) {
    case CHART_TERM_UNIT.HOUR:
      return 72;
    case CHART_TERM_UNIT.DAY:
      return 90;
    case CHART_TERM_UNIT.WEEK:
      return 12;
    case CHART_TERM_UNIT.MONTH:
      return 3;
    case CHART_TERM_UNIT.QUARTER:
      return 1;
  }
};

const validateTermLength = (
  value: number,
  unit: CHART_TERM_UNIT,
): {
  validate: boolean;
  message?: string;
} => {
  const unitText = ChartTermUnitMap.get(unit);
  return {
    validate: value <= getMaxRangeByTermUnit(unit),
    message: `Max range on ${unitText} is ${getMaxRangeByTermUnit(unit)}.`,
  };
};

const getSortedSingleTable = (
  data: ChartSingleTableRes,
  candidates: StringNumberValue[],
): ChartSingleTableRes => {
  let fullItems: StringNumberValue[] = [];
  const primaryTable = data.tables[0] || [];
  const sortedTable = [...primaryTable].sort((a, b) => b.value - a.value);
  sortedTable.forEach((item) => {
    fullItems.push(item.x);
  });

  // Compare tables 처리
  data.compare_tables?.[0]?.forEach((item) => {
    if (!fullItems.includes(item.x)) {
      fullItems.push(item.x);
    }
  });

  candidates.forEach((candidate) => {
    if (!fullItems.includes(candidate)) {
      fullItems.push(candidate);
    }
  });

  const createSortedTable = (sourceTable: typeof primaryTable) => {
    return fullItems.map((item) => {
      const currentItem = sourceTable.find((xItem) => xItem.x === item);
      if (!currentItem) {
        return {
          x: item,
          value: 0,
        };
      }
      return currentItem;
    });
  };

  const newTables = data.tables.map(createSortedTable);
  const newCompareTables = data.compare_tables?.map(createSortedTable);

  return {
    ...data,
    tables: newTables,
    compare_tables: newCompareTables,
  };
};

const getSortedCrossTable = (
  data: ChartCrossTableRes,
  candidates: StringNumberValue[],
): ChartCrossTableRes => {
  let fullItems: StringNumberValue[] = [];
  const primaryTable = data.tables[0] || [];
  const sortedTable = [...primaryTable].sort(
    (a, b) =>
      b.values?.reduce((acc, curr) => acc + curr.value, 0) -
      a.values?.reduce((acc, curr) => acc + curr.value, 0),
  );
  sortedTable.forEach((item) => {
    fullItems.push(item.x);
  });

  // Compare tables 처리
  data.compare_tables?.[0]?.forEach((item) => {
    if (!fullItems.some((x) => x.toString() === item.x.toString())) {
      fullItems.push(item.x);
    }
  });

  candidates.forEach((candidate) => {
    if (!fullItems.some((x) => x.toString() === candidate.toString())) {
      fullItems.push(candidate);
    }
  });

  const createSortedTable = (sourceTable: typeof primaryTable) => {
    return fullItems.map((item) => {
      const currentItem = sourceTable.find((xItem) => xItem.x === item);
      if (!currentItem) {
        return {
          x: item,
          values: [],
        };
      }
      return currentItem;
    });
  };

  const newTables = data.tables.map(createSortedTable);
  const newCompareTables = data.compare_tables?.map(createSortedTable);

  return {
    ...data,
    tables: newTables,
    compare_tables: newCompareTables,
  };
};

const getSortedRowItems = (
  chart: ChartBase,
  tableData: ChartTableRes,
  candidates: StringNumberValue[],
  isCandidateTable?: boolean,
): ChartTableRes => {
  const type = getTableType(chart.type, chart.historical.value);
  const shouldFilterUserDefinedEntireSelection =
    !isCandidateTable &&
    chart.field.entire &&
    chart.selected_field.method ===
      CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED &&
    candidates.length > 0;

  if (type === 'single') {
    const data = { ...tableData } as ChartSingleTableRes;
    const sortedData = getSortedSingleTable(data, candidates);
    if (shouldFilterUserDefinedEntireSelection) {
      return {
        ...sortedData,
        tables: sortedData.tables.map((table) =>
          table.filter((item) => candidates.includes(item.x)),
        ) as ChartSingleTableData[],
        compare_tables: sortedData.compare_tables?.map((table) =>
          table.filter((item) => candidates.includes(item.x)),
        ) as ChartSingleTableData[],
      };
    }

    if (chart.field.entire) {
      const topNumber = ChartTopNumberMap.get(chart.selected_field.method);
      return {
        ...sortedData,
        tables: sortedData.tables.map((table) =>
          table.slice(0, topNumber),
        ) as ChartSingleTableData[],
        compare_tables: sortedData.compare_tables?.map((table) =>
          table.slice(0, topNumber),
        ) as ChartSingleTableData[],
      };
    } else {
      if (!isCandidateTable) {
        if (
          chart.selected_field.method !==
          CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED
        ) {
          const topNumber = ChartTopNumberMap.get(chart.selected_field.method);
          // Top인경우 candidate를 대상으로 정렬데이터 다시 추출;
          const sortedData = getSortedSingleTable(
            data,
            chart.field.candidates || [],
          );
          return {
            ...sortedData,
            tables: sortedData.tables.map((table) =>
              table.slice(0, topNumber),
            ) as ChartSingleTableData[],
            compare_tables: sortedData.compare_tables?.map((table) =>
              table.slice(0, topNumber),
            ) as ChartSingleTableData[],
          };
        } else {
          return {
            ...sortedData,
            tables: sortedData.tables.map((table) =>
              table.filter((item) => candidates.includes(item.x)),
            ) as ChartSingleTableData[],
            compare_tables: sortedData.compare_tables?.map((table) =>
              table.filter((item) => candidates.includes(item.x)),
            ) as ChartSingleTableData[],
          };
        }
      }
      return sortedData as ChartTableRes;
    }
  } else {
    const data = { ...tableData } as ChartCrossTableRes;
    const sortedData = getSortedCrossTable(data, candidates);
    if (chart.type === CHART_TYPE.SINGLE_AXIS) {
      if (shouldFilterUserDefinedEntireSelection) {
        return {
          ...sortedData,
          tables: sortedData.tables.map((table) =>
            table.filter((item) => candidates.includes(item.x)),
          ) as ChartCrossTableData[],
          compare_tables: sortedData.compare_tables?.map((table) =>
            table.filter((item) => candidates.includes(item.x)),
          ) as ChartCrossTableData[],
        };
      }

      if (chart.field.entire) {
        const topNumber = ChartTopNumberMap.get(chart.selected_field.method);
        return {
          ...sortedData,
          tables: sortedData.tables.map((table) =>
            table.slice(0, topNumber),
          ) as ChartCrossTableData[],
          compare_tables: sortedData.compare_tables?.map((table) =>
            table.slice(0, topNumber),
          ) as ChartCrossTableData[],
        };
      } else {
        if (!isCandidateTable) {
          if (
            chart.selected_field.method !==
            CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED
          ) {
            const topNumber = ChartTopNumberMap.get(
              chart.selected_field.method,
            );
            // Top인경우 candidate를 대상으로 정렬데이터 다시 추출;
            const sortedData = getSortedCrossTable(
              data,
              chart.field.candidates || [],
            );
            return {
              ...sortedData,
              tables: sortedData.tables.map((table) =>
                table.slice(0, topNumber),
              ) as ChartCrossTableData[],
              compare_tables: sortedData.compare_tables?.map((table) =>
                table.slice(0, topNumber),
              ) as ChartCrossTableData[],
            };
          } else {
            return {
              ...sortedData,
              tables: sortedData.tables.map((table) =>
                table.filter((item) => candidates.includes(item.x)),
              ) as ChartCrossTableData[],
              compare_tables: sortedData.compare_tables?.map((table) =>
                table.filter((item) => candidates.includes(item.x)),
              ) as ChartCrossTableData[],
            };
          }
        }
        return sortedData as ChartTableRes;
      }
    }
    if (chart.type === CHART_TYPE.CROSS_AXIS) {
      const sortedData = getSortedCrossTable(data, candidates);
      return sortedData as ChartTableRes;
    }
    return sortedData as ChartTableRes;
  }
};

const getReadableDateRange = (dateRange: [Dayjs, Dayjs]): [string, string] => {
  const [startDate, endDate] = dateRange;
  return [
    startDate.format(DATE_FORMAT.readable),
    endDate.format(DATE_FORMAT.readable),
  ];
};

const getReadableDateRangeByTerm = (term: ChartTermDef): string => {
  if (term.type === CHART_TERM_TYPE.FIXED) {
    const [start, end] = getDateRangeWithEndDateOffset([
      getTimezoneDayjs(term.start_date),
      getTimezoneDayjs(term.end_date),
    ]);

    return `${start.format(DATE_FORMAT.readable)} - ${end.format(
      DATE_FORMAT.readable,
    )}`;
  }

  const dateRange = getTimeRangeByTerm(getTimezoneDayjs(), term);
  const offsetDateRange = getDateRangeWithEndDateOffset(dateRange);

  const readableDateRange = getReadableDateRange(offsetDateRange);
  return `${readableDateRange[0]} - ${readableDateRange[1]}`;
};

const getReadableDateRangeByCompareTerm = (
  term: ChartTermDef,
  compareTerm?: ChartCompareTermDef,
): string | null => {
  if (compareTerm) {
    const compareDateRange = getCompareTimeRangeByTerm(
      getTimezoneDayjs(),
      term,
      compareTerm,
    );

    const offsetCompareDateRange =
      getDateRangeWithEndDateOffset(compareDateRange);

    const readableDateRange = getReadableDateRange(offsetCompareDateRange);
    return `${readableDateRange[0]} - ${readableDateRange[1]}`;
  }
  return null;
};

const getCustomCompareTermEndDate = (date: Dayjs, term: ChartTermDef) => {
  if (term.type === CHART_TERM_TYPE.FIXED) {
    const opUnit = getOpUnitTypeByTermUnit(term.unit);
    const termDiff = getTimezoneDayjs(term.end_date).diff(
      getTimezoneDayjs(term.start_date),
      opUnit,
    );
    const endDate = date.addVal(termDiff, opUnit).endOf(opUnit).startOf('day');
    return endDate;
  }
  if (term.type === CHART_TERM_TYPE.PERIODIC) {
    if (term.unit === CHART_TERM_UNIT.QUARTER) {
      return date.addQuarters(term.value);
    }
    return date.addVal(term.value, getOpUnitTypeByTermUnit(term.unit));
  }
  return date;
};

const getTermString = (term: ChartTermDef) => {
  if (term.type === CHART_TERM_TYPE.FIXED) {
    return 'Custom';
  }

  const unit = getTermUnitLabel(term.unit);

  const hasS = term.value > 1 ? 's' : '';

  const today = term.unit === CHART_TERM_UNIT.DAY ? 'today' : `this ${unit}`;

  const relative =
    term.relative === CHART_PERIOD_RELATIVE.INCLUDE ? 'including' : 'excluding';

  return `Last ${term.value} ${unit}${hasS} ${relative} ${today}`;
};

const getChartFeedbackListParams = (
  chart: ChartBase,
  tableRes: ChartTableRes,
) => {
  const getItemX = () => {
    if (chart.type === CHART_TYPE.SINGLE_AXIS) {
      const tableData = tableRes as ChartSingleTableRes;
      const topNum = ChartTopNumberMap.get(chart.selected_field.method);
      const tableItems = tableData.tables[0] || [];

      // Top tag 같이 entire: true인 경우 candidate, selected_field.items가 존재하지 않으므로
      // Table 응답값으로 x_items 추출
      if (chart.field.entire) {
        const xItems = tableItems.slice(0, topNum).map((item) => item.x);
        return xItems;
      }

      // selected_field.method가 Top인 경우 candidate를 대상으로 정렬데이터 다시 추출;
      // Table 응답값에 존재하지 않는 경우 0으로 채우고 정렬
      if (
        chart.selected_field.method !==
        CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED
      ) {
        const xItems = filterMap(chart.field.candidates || [], (candidate) => {
          const target = tableItems.find((item) => item.x === candidate);
          if (!target)
            return {
              x: candidate,
              value: 0,
            };
          return {
            x: target.x,
            value: target.value,
          };
        })
          .sort((a, b) => b.value - a.value)
          .map((item) => item.x)
          .slice(0, topNum);
        return xItems;
      }
      // selected_field.method가 User Defined인 경우 selected_field.items 추출
      return chart.selected_field.items;
    }
    if (chart.type === CHART_TYPE.CROSS_AXIS) {
      if (chart.field.type === CHART_FIELD.FEEDBACK_ALL) {
        return tableRes.tables[0]?.map((item) => item.x) || [];
      }
      return chart.field.candidates;
    }
  };
  const getItemY = () => {
    if (chart.type === CHART_TYPE.CROSS_AXIS) {
      return chart.series[0]?.cross_field?.candidates;
    }
    return [];
  };
  const itemX = getItemX();
  const itemY = getItemY();
  const crossField = chart.series[0]?.cross_field;

  return {
    itemX,
    itemY,
    crossField,
  };
};

const getChartTermValueByTermType = (
  term: ChartTermDef,
  isHistorical: boolean,
): ChartTermDef => {
  if (term.type === CHART_TERM_TYPE.FIXED) {
    // 고정 기간인 경우 기간 길이를 계산하여 value로 설정
    let startDate: Dayjs = getTimezoneDayjs(term.start_date);
    let endDate: Dayjs = getTimezoneDayjs(term.end_date);

    const unit: CHART_TERM_UNIT = isHistorical
      ? getUnitByFrequency(getFrequencyByTermUnit(term.unit))
      : CHART_TERM_UNIT.DAY;

    // unit에 날짜 보정
    switch (unit) {
      case CHART_TERM_UNIT.DAY:
        startDate = getTimezoneDayjs(term.start_date);
        endDate =
          endDate.date() === 1
            ? endDate
            : endDate.startOf('date').addVal(1, 'day');
        break;
      case CHART_TERM_UNIT.WEEK:
        startDate = getTimezoneDayjs(term.start_date).startOf('week');
        endDate = endDate.startOf('week').isSame(endDate)
          ? endDate
          : endDate.startOf('week').addVal(1, 'week');
        break;
      case CHART_TERM_UNIT.MONTH:
        startDate = getTimezoneDayjs(term.start_date).startOf('month');
        endDate = endDate.startOf('month').isSame(endDate)
          ? endDate
          : endDate.startOf('month').addVal(1, 'month');
        break;
      case CHART_TERM_UNIT.QUARTER:
        startDate = getTimezoneDayjs(term.start_date).startOfQuarter();
        endDate = endDate.startOfQuarter().isSame(endDate)
          ? endDate
          : endDate.startOfQuarter().addQuarters(1);
        break;
      default:
        throw new Error('Invalid term unit');
    }

    const diff = endDate.diff(startDate, getOpUnitTypeByTermUnit(unit));
    const value = diff;
    return {
      ...term,
      unit,
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
      value,
    };
  }
  return term;
};

// x축 디스플레이용 context-aware 포맷 (raw label은 변경하지 않고 표시만 변환)
export const formatDisplayLabel = (
  rawLabel: string,
  frequency: CHART_FREQUENCY,
  previousVisibleRawLabel: string | null,
): string => {
  if (frequency === CHART_FREQUENCY.WEEK) return rawLabel;

  switch (frequency) {
    case CHART_FREQUENCY.HOUR: {
      const parsed = dayjs(rawLabel, 'YYYY-MM-DD HH');
      if (!parsed.isValid()) return rawLabel;
      return parsed.format('HH');
    }
    case CHART_FREQUENCY.DAY: {
      const parsed = dayjs(rawLabel, 'YYYY-MM-DD');
      if (!parsed.isValid()) return rawLabel;
      // 연도 변경 시에만 연도 포함, 그 외는 항상 MMM D 표기
      if (previousVisibleRawLabel) {
        const prevParsed = dayjs(previousVisibleRawLabel, 'YYYY-MM-DD');
        if (prevParsed.isValid() && parsed.year() !== prevParsed.year()) {
          return parsed.format("'YY MMM D");
        }
      }
      return parsed.format('MMM D');
    }
    case CHART_FREQUENCY.MONTH: {
      const parsed = dayjs(rawLabel, 'MMM YY');
      if (!parsed.isValid()) return rawLabel;
      if (!previousVisibleRawLabel) return parsed.format('YYYY-MM');
      const prevParsed = dayjs(previousVisibleRawLabel, 'MMM YY');
      if (!prevParsed.isValid() || parsed.year() !== prevParsed.year()) {
        return parsed.format('YYYY-MM');
      }
      return parsed.format('MM');
    }
    default:
      return rawLabel;
  }
};

// 테이블 컬럼 헤더용 배치 포맷 (모든 라벨이 순차적으로 보이는 경우)
export const formatDisplayLabelsForTable = (
  rawLabels: string[],
  frequency: CHART_FREQUENCY,
): string[] => {
  return rawLabels.map((label, index) => {
    const previousLabel = index > 0 ? rawLabels[index - 1] : null;
    return formatDisplayLabel(label, frequency, previousLabel);
  });
};

const chartUtils = {
  getFrequencyByTermUnit,
  getTimeRangeByTerm,
  getCompareTimeRangeByTerm,
  transformToYBased,
  getDateLabelsByUnit,
  parseDateFormatByFrequency,
  getFormatByFrequency,
  getKeyValueMapByKeys,
  getUnitByFrequency,
  getColorScaleColor,
  formatNumber,
  getTableType,
  validateTermLength,
  getSortedSingleTable,
  getSortedCrossTable,
  getSortedRowItems,
  getReadableDateRange,
  getReadableDateRangeByTerm,
  getReadableDateRangeByCompareTerm,
  getCustomCompareTermEndDate,
  getTermString,
  getMaxRangeByTermUnit,
  getChartFeedbackListParams,
  getChartTermValueByTermType,
  formatDisplayLabel,
  formatDisplayLabelsForTable,
};

export default chartUtils;
